import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { browserHistory } from '@browserHistory';
import { logout } from './../../../actions/authActions';
import Globalize from '../../../actions/languages/Globalize';
import MenuSearchAndFilter from '../../common/MenuSearchAndFilter';
import Loader from '../Loader/Loader';
import { useFeatures } from '../../../contexts/FeaturesContext';
import { getRedirectPath } from '../../../utils/helpers';
import PermissionsModal from './PermissionsModal';

const DashBoardNavigation = (props) => {
  const [sidebarOpen, setSidebarOpen] = useState(props?.sidebarOpen || true);
  const [userProfileDropdownOpen, setUserProfileDropdownOpen] = useState(false);
  const [helpDropdownOpen, setHelpDropdownOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const userProfileRef = useRef(null);
  const helpRef = useRef(null);
  const { features } = useFeatures();
  const location = useLocation();

  const handleLogout = (e) => {
    e.preventDefault();
    const { clearUser } = props;
    if (clearUser) {
      clearUser();
    }
    logout()().then(() => {
      const redirectPath = getRedirectPath(location.pathname);
      browserHistory.push('/login?redirect=' + encodeURIComponent(redirectPath));
    });
  };

  const toggleSideBar = () => {
    setSidebarOpen((prevState) => {
      const newState = !prevState;
      if (props.toggleSideBar) {
        props.toggleSideBar();
      }
      return newState;
    });
  };

  const toggleUserProfileDropdown = () => {
    setUserProfileDropdownOpen((prevState) => !prevState);
  };

  const handleUserProfileClick = (e) => {
    e.stopPropagation();
    toggleUserProfileDropdown();
  };

  const toggleHelpDropdown = () => {
    setHelpDropdownOpen((prevState) => !prevState);
  };

  const handleHelpClick = (e) => {
    e.stopPropagation();
    toggleHelpDropdown();
  };

  const handlePermissionsClick = (e) => {
    e.stopPropagation();
    setPermissionsModalOpen(true);
    setHelpDropdownOpen(false);
  };

  const closePermissionsModal = () => {
    setPermissionsModalOpen(false);
  };

  // Get service owner from current route
  const currentRoute = location.pathname;
  const currentFeature = features?.find(
    (feature) => feature?.route === currentRoute
  );
  const serviceOwner = currentFeature?.service_owner || '@demand-web-oncall';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        userProfileRef.current &&
        !userProfileRef.current.contains(event.target)
      ) {
        setUserProfileDropdownOpen(false);
      }
      if (helpRef.current && !helpRef.current.contains(event.target)) {
        setHelpDropdownOpen(false);
      }
    };

    if (userProfileDropdownOpen || helpDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userProfileDropdownOpen, helpDropdownOpen]);

  const isSideBar = props?.resource && props?.action;

  return (
    <nav
      className="navbar navbar-default navbar-static-top justify-content-between"
      role="navigation"
    >
      <div className="flex-center">
        <div className="navbar-header flex-center">
          <Link to="/" className="navbar-brand">
            <img
              src="https://images.meesho.com/images/marketing/1759392539099.png"
              alt="Meesho Supply"
              height={25}
              width={120}
            />
          </Link>
        </div>
        {isSideBar && (
          <div className="toggle-button">
            <div onClick={toggleSideBar} id="menu_toggle">
              {!sidebarOpen ? (
                <i className="fa fa-bars" />
              ) : (
                <i className="fa fa-times" />
              )}
            </div>
          </div>
        )}
      </div>
      <div className="flex">
        <ul className="nav navbar-top-links navbar-right flex-center">
          {props?.user?.email && (
            <li className="user-name">
              <div className="user-email">
                <span>{props?.user?.email}</span>
              </div>
            </li>
          )}
          <li
            className="user-name help-dropdown-container"
            ref={helpRef}
            onMouseEnter={() => setHelpDropdownOpen(true)}
            onMouseLeave={() => setHelpDropdownOpen(false)}
          >
            <div className="help-icon-wrapper" onClick={handleHelpClick}>
              <i
                className="fa fa-question-circle"
                style={{
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#495057',
                }}
              />
            </div>
            {helpDropdownOpen && (
              <ul className="help-dropdown-menu">
                <li className="help-dropdown-item">
                  <div className="help-message">
                    <i
                      className="fa fa-info-circle"
                      style={{ color: '#007bff' }}
                    />
                    <span>
                      Facing any technical issue? Tag below mentioned service
                      owner on Slack:
                    </span>
                  </div>
                  <div className="help-service-owners">
                    <span className="help-service-owner-tag">
                      {serviceOwner}
                    </span>
                  </div>
                </li>
                <li
                  className="help-dropdown-item help-permissions-item"
                  onClick={handlePermissionsClick}
                >
                  <div className="help-message">
                    <i className="fa fa-shield" style={{ color: '#ec4899' }} />
                    <span>Know More on Permissions</span>
                  </div>
                </li>
              </ul>
            )}
          </li>
          <li
            className="user-name user-profile-dropdown-container"
            ref={userProfileRef}
            onMouseEnter={() => setUserProfileDropdownOpen(true)}
            onMouseLeave={() => setUserProfileDropdownOpen(false)}
          >
            <div
              className="user-profile-icon-wrapper"
              onClick={handleUserProfileClick}
            >
              <i
                className="fa fa-user-circle"
                style={{
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#495057',
                }}
              />
            </div>
            {userProfileDropdownOpen && (
              <ul className="user-profile-dropdown-menu">
                {props?.user?.email && (
                  <li className="user-profile-dropdown-item">
                    <div className="user-profile-email">
                      <i
                        className="fa fa-envelope"
                        style={{ marginRight: '8px', color: '#6c757d' }}
                      />
                      <span>{props.user.email}</span>
                    </div>
                    {props?.user?.country && (
                      <div className="user-profile-country">
                        <i
                          className="fa fa-globe"
                          style={{ marginRight: '8px', color: '#6c757d' }}
                        />
                        <span>{props.user.country.name}</span>
                      </div>
                    )}
                  </li>
                )}
                <li
                  className="user-profile-dropdown-item user-profile-logout"
                  onClick={handleLogout}
                >
                  <i
                    className="fa fa-sign-out"
                    style={{ marginRight: '8px' }}
                  />
                  <span>{Globalize.getString('logout')}</span>
                </li>
              </ul>
            )}
          </li>
        </ul>
      </div>
      {isSideBar && (
        <div
          className={
            'navbar-default-new  sidebar sidebar-open' +
            (sidebarOpen ? '' : ' sidebar-close')
          }
          role="navigation"
        >
          {props.isFeaturesLoading ? (
            <Loader />
          ) : (
            <MenuSearchAndFilter from="dashboardNavigation" {...props} />
          )}
        </div>
      )}

      {/* Permissions Modal */}
      <PermissionsModal
        isOpen={permissionsModalOpen}
        onClose={closePermissionsModal}
        resource={props?.resource}
        action={props?.action}
        user={props?.user}
      />
    </nav>
  );
};

export default DashBoardNavigation;
